import React from "react";
import { Card, CardContent, Typography, Box, Divider } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { ProjectedFigures } from "../types";
import { formatCurrency } from "../utils/calculations";

interface ResultsDisplayProps {
  projections: ProjectedFigures[];
  totalMarketValue: number;
  riskAdjustedValue?: number;
}

export const ResultsDisplay: React.FC<ResultsDisplayProps> = ({
  projections,
  totalMarketValue,
  riskAdjustedValue,
}) => {
  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Market Value Analysis
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Total Market Value: {formatCurrency(totalMarketValue)}
          </Typography>
          {riskAdjustedValue !== undefined && (
            <Typography variant="h6" color="secondary" gutterBottom>
              Risk-Adjusted Value: {formatCurrency(riskAdjustedValue)}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Revenue Projections
        </Typography>

        <Box sx={{ height: 400, mt: 2 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={projections}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="year"
                label={{ value: "Year", position: "insideBottom", offset: -5 }}
              />
              <YAxis
                tickFormatter={(value) => formatCurrency(value)}
                width={150}
                label={{
                  value: "Revenue",
                  angle: -90,
                  position: "insideLeft",
                  dx: -16, // Adjust horizontal offset to expand and show text
                }}
              />
              <Tooltip
                formatter={(value: number) => [formatCurrency(value)]}
                labelFormatter={(label) => `Year ${label}`}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="revenue"
                name="Annual Revenue"
                stroke="#1976d2"
                activeDot={{ r: 8 }}
              />
              <Line
                type="monotone"
                dataKey="cumulativeRevenue"
                name="Cumulative Revenue"
                stroke="#9c27b0"
                activeDot={{ r: 8 }}
              />
              <Line
                type="monotone"
                dataKey="economicImpact"
                name="Economic Impact"
                stroke="#2e7d32"
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Detailed Projections
        </Typography>

        <Box sx={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: "8px", textAlign: "left" }}>Year</th>
                <th style={{ padding: "8px", textAlign: "right" }}>
                  Annual Revenue
                </th>
                <th style={{ padding: "8px", textAlign: "right" }}>
                  Cumulative Revenue
                </th>
                <th style={{ padding: "8px", textAlign: "right" }}>
                  Economic Impact
                </th>
              </tr>
            </thead>
            <tbody>
              {projections
                .filter((proj) => proj.year !== 0)
                .map((projection) => (
                  <tr
                    key={projection.year}
                    style={{ borderBottom: "1px solid #ddd" }}
                  >
                    <td style={{ padding: "8px" }}>{projection.year}</td>
                    <td style={{ padding: "8px", textAlign: "right" }}>
                      {formatCurrency(projection.revenue)}
                    </td>
                    <td style={{ padding: "8px", textAlign: "right" }}>
                      {formatCurrency(projection.cumulativeRevenue)}
                    </td>
                    <td style={{ padding: "8px", textAlign: "right" }}>
                      {formatCurrency(projection.economicImpact)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Box>
      </CardContent>
    </Card>
  );
};
